import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { H1, H2 } from '@/app/ui/components/heading';
import {
  AmbiguityTypes,
  AnalysisDocumentTypes,
  ClauseScannerAnalysisType,
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  InconsistencyTypes,
  StandardDeviationsTypes,
  SummarizationNamespaces,
  queryKeys,
} from '@/app/utils/constants';
import {
  LinkSquare02Icon,
  SearchFocusIcon,
  SettingsIcon,
  NewOfficeIcon,
  FolderIcon,
  Clock04Icon,
  TimeQuarterPassIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import AnalysisAssessment from '@/app/components/features/evaluations/analysis/NewAssesment/AnalysisAssessment';
import {
  MainContainer,
  MainFrames,
  MainHeader,
  MainNav,
  MainNavButton,
  MainNavItem,
  MainNavLink,
  MainNavList,
  MainScrollArea,
} from '@/app/ui/components/main';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbMenu,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { Button } from '@/app/ui/components/button';
import { AppContext } from '@/app/appContext';
import { use, useEffect } from 'react';
import { AmbiguitiesFrame } from '@/app/components/features/evaluations/analysis/Ambiguities/AmbiguitiesFrame';
import { StandardDeviationsFrame } from '@/app/components/features/evaluations/frames/StandardDeviationsFrame';
import { ConflictsFrame } from '@/app/components/features/evaluations/frames/ConflictsFrame';
import { ClausesFrame } from '@/app/components/features/evaluations/frames/ClausesFrame';
import { SummaryFrame } from '@/app/components/features/evaluations/frames/SummaryFrame';
import { ExtractionsFrame } from '@/app/components/features/evaluations/frames/ExtractionsFrame';
import { WorkPackagesFrame } from '@/app/components/features/evaluations/frames/WorkPackagesFrame';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { OrganizationAvatar } from '@/app/ui/components/organization-avatar';
import { BidDraftingFrame } from '@/app/components/features/bids/BidDraftingFrame';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { useSuspenseQuery, useQuery } from '@tanstack/react-query';
import { getLatestEvaluationVersion } from '@/app/services/versioningService';
import { useAtom, useSetAtom } from 'jotai';
import { versionPanelOpenAtom } from '@/app/components/features/evaluations/VersionHistoryPanel/store';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import { sidechatOpenAtom } from '@/app/components/shared/Sidechat/store';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tGeneral = useTranslations('general');
  const tBids = useTranslations('pages.bids');
  const { userMeta } = use(AppContext);
  const organizationName = userMeta?.organizationName || '';
  const { projectId, tenderPackageId } = Route.useParams();
  const navigate = useNavigate();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));

  const { data: documents } = useEntityDocuments(tenderPackageId, DocumentTypes.TENDER_PACKAGE);

  const { data: latestVersion } = useQuery({
    queryKey: [queryKeys.EVALUATION_LATEST_VERSION, tenderPackageId],
    queryFn: async () => (await getLatestEvaluationVersion(tenderPackageId)).data,
  });

  const [versionPanelOpen, setVersionPanelOpen] = useAtom(versionPanelOpenAtom);
  const setSidechatOpen = useSetAtom(sidechatOpenAtom);

  useEffect(() => () => setVersionPanelOpen(false), [setVersionPanelOpen]);

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea className="relative">
        <DocumentAlerts
          resourceId={tenderPackageId}
          summaryType={SummarizationNamespaces.TENDER_PACKAGE}
          onGoToFiles={() =>
            navigate({
              to: '/project/$projectId/tender-packages/$tenderPackageId/files',
              params: { projectId, tenderPackageId },
            })
          }
        />
        <MainHeader>
          <H1>{t('titleLong')}</H1>
        </MainHeader>
        <MainNav>
          <MainNavList>
            {evaluation.companyName && (
              <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                {organizationName === evaluation.companyName ? (
                  <OrganizationAvatar
                    organization={{ id: userMeta.organizationId ?? '', name: organizationName }}
                    size={24}
                    className="rounded-[calc(var(--radius-md)-0.125rem)]"
                  />
                ) : (
                  <HugeiconsIcon icon={NewOfficeIcon} />
                )}
                {evaluation.companyName}{' '}
                {organizationName === evaluation.companyName ? `(${tGeneral(`yourOrg`)})` : null}
              </MainNavItem>
            )}
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/tender-packages/$tenderPackageId/files"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={FolderIcon} />
                {t('nav.files')} {!documents?.length ? null : `(${documents.length})`}
              </MainNavLink>
            </MainNavItem>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/tender-packages/$tenderPackageId/activity"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={Clock04Icon} />
                {t('nav.activityLog')}
              </MainNavLink>
            </MainNavItem>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/tender-packages/$tenderPackageId/settings"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SettingsIcon} />
                {t('nav.settings')}
              </MainNavLink>
            </MainNavItem>
            {latestVersion && (
              <MainNavItem>
                <MainNavButton
                  data-selected={versionPanelOpen ? '' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    setVersionPanelOpen((prev) => !prev);
                    if (!versionPanelOpen) setSidechatOpen(false);
                  }}
                >
                  <HugeiconsIcon icon={TimeQuarterPassIcon} />
                  {t('nav.versionHistory')} (v{latestVersion.version.versionNumber}{' '}
                  {latestVersion.version.status === 'active' ? tGeneral('latest') : ''})
                </MainNavButton>
              </MainNavItem>
            )}
          </MainNavList>
        </MainNav>
        <MainContainer>
          <H2 className="mb-3">{t('section1Title')}</H2>
          <AnalysisAssessment
            documentType={AnalysisDocumentTypes.TENDER_PACKAGE}
            id={tenderPackageId}
          />
          <H2 className="mb-3 mt-5">{t('section2Title')}</H2>
          <MainFrames>
            <SummaryFrame
              to={'/project/$projectId/tender-packages/$tenderPackageId/summary'}
              params={{ projectId, tenderPackageId }}
              evaluationId={tenderPackageId}
              summaryType={SummarizationNamespaces.TENDER_PACKAGE}
            />
            <WorkPackagesFrame
              type={'TenderPackage'}
              evaluationId={tenderPackageId}
              evalLanguage={evaluation.language}
            />
            <ExtractionsFrame
              to={'/project/$projectId/tender-packages/$tenderPackageId/extractions'}
              params={{ projectId, tenderPackageId }}
              evaluationId={tenderPackageId}
              summaryType={SummarizationNamespaces.TENDER_PACKAGE}
              evalLanguage={evaluation.language}
            />
          </MainFrames>
          <H2 className="mb-3 mt-5">{t('section3Title')}</H2>
          <MainFrames>
            <ClausesFrame
              evaluationId={tenderPackageId}
              clauseScannerType={ClauseScannerAnalysisType.TENDER_PACKAGE}
              to={'/project/$projectId/tender-packages/$tenderPackageId/clauses'}
              params={{ projectId, tenderPackageId }}
            />
            <AmbiguitiesFrame
              ambiguityType={AmbiguityTypes.TENDER_PACKAGE}
              id={tenderPackageId}
              to={'/project/$projectId/tender-packages/$tenderPackageId/ambiguities'}
              params={{ projectId, tenderPackageId }}
            />
            <ConflictsFrame
              evaluationId={tenderPackageId}
              inconsistencyType={InconsistencyTypes.TENDER_PACKAGE}
              to={'/project/$projectId/tender-packages/$tenderPackageId/conflicts'}
              params={{ projectId, tenderPackageId }}
            />
            <StandardDeviationsFrame
              evaluationId={tenderPackageId}
              evalLanguage={evaluation.language}
              deviationsType={StandardDeviationsTypes.TENDER_PACKAGE}
              to={'/project/$projectId/tender-packages/$tenderPackageId/standard-deviations'}
              params={{ projectId, tenderPackageId }}
            />
          </MainFrames>
          <div className="flex mb-3 justify-between items-center mt-5">
            <H2>{tBids('cta')}</H2>
            <Button
              size="sm"
              variant="ghost"
              className="md:text-sm"
              nativeButton={false}
              render={
                <Link
                  to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                  params={{ projectId, tenderPackageId }}
                >
                  {tBids('ctaLink')}
                  <HugeiconsIcon icon={LinkSquare02Icon} />
                </Link>
              }
            />
          </div>
          <BidDraftingFrame />
        </MainContainer>
      </MainScrollArea>
    </>
  );
}
